import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Loader2,
  MapPin,
  Plus,
  ShieldCheck,
  Trash2,
  UserPlus,
} from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';

type ActiveTab = 'login' | 'register';
type ApplicationType = 'RENTAL_TENANT' | 'PROPERTY_SELLER';
type MaritalStatus = 'SINGLE' | 'MARRIED' | 'DIVORCED' | 'WIDOWED' | 'STABLE_UNION';

interface ApiError {
  response?: {
    data?: {
      message?: string | string[];
    };
  };
}

interface ViaCepResponse {
  cep?: string;
  logradouro?: string;
  complemento?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean;
}

interface PetForm {
  name: string;
  type: string;
  breed: string;
  notes: string;
}

interface RegisterForm {
  applicationType: ApplicationType;
  fullName: string;
  email: string;
  phone: string;
  cpf: string;
  rg: string;
  driverLicense: string;
  motherName: string;
  maritalStatus: MaritalStatus | '';
  spouseName: string;
  childrenNamesInput: string;
  stepchildrenNamesInput: string;
  relativesNotes: string;
  hasPets: boolean;
  pets: PetForm[];
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  employmentInfo: string;
  monthlyIncome: string;
  intendedOccupants: string;
  rentalGuaranteePreference: string;
  propertyRegistrationNumber: string;
  hasActiveDebt: boolean;
  activeDebtDetails: string;
  isInPartition: boolean;
  isInInventory: boolean;
  legalIssuesDetails: string;
  propertyDocumentationNotes: string;
  additionalComplianceInfo: string;
}

const initialRegisterForm: RegisterForm = {
  applicationType: 'RENTAL_TENANT',
  fullName: '',
  email: '',
  phone: '',
  cpf: '',
  rg: '',
  driverLicense: '',
  motherName: '',
  maritalStatus: '',
  spouseName: '',
  childrenNamesInput: '',
  stepchildrenNamesInput: '',
  relativesNotes: '',
  hasPets: false,
  pets: [{ name: '', type: '', breed: '', notes: '' }],
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  employmentInfo: '',
  monthlyIncome: '',
  intendedOccupants: '',
  rentalGuaranteePreference: '',
  propertyRegistrationNumber: '',
  hasActiveDebt: false,
  activeDebtDetails: '',
  isInPartition: false,
  isInInventory: false,
  legalIssuesDetails: '',
  propertyDocumentationNotes: '',
  additionalComplianceInfo: '',
};

function parseError(error: unknown, fallback: string): string {
  const typedError = error as ApiError;
  const message = typedError?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim().length > 0) return message;
  return fallback;
}

function splitCommaList(input: string): string[] {
  return input
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export default function LoginPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('login');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoWidth, setLogoWidth] = useState<number>(320);

  useEffect(() => {
    api
      .get('/branding/public')
      .then(({ data }) => {
        if (data?.logoUrl) setLogoUrl(data.logoUrl);
        if (data?.sizes?.loginLogoWidth) setLogoWidth(data.sizes.loginLogoWidth);
        if (data?.faviconUrl) {
          let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = data.faviconUrl;
        }
      })
      .catch(() => {});
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [registerData, setRegisterData] = useState<RegisterForm>(initialRegisterForm);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (error: unknown) {
      setLoginError(parseError(error, 'Erro ao fazer login.'));
    } finally {
      setLoginLoading(false);
    }
  };

  const updateField = <K extends keyof RegisterForm>(key: K, value: RegisterForm[K]) => {
    setRegisterData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updatePet = <K extends keyof PetForm>(index: number, key: K, value: PetForm[K]) => {
    setRegisterData((prev) => {
      const pets = [...prev.pets];
      const currentPet = pets[index] ?? { name: '', type: '', breed: '', notes: '' };
      pets[index] = {
        ...currentPet,
        [key]: value,
      };
      return { ...prev, pets };
    });
  };

  const addPet = () => {
    setRegisterData((prev) => ({
      ...prev,
      pets: [...prev.pets, { name: '', type: '', breed: '', notes: '' }],
    }));
  };

  const removePet = (index: number) => {
    setRegisterData((prev) => {
      const pets = prev.pets.filter((_, petIndex) => petIndex !== index);
      return {
        ...prev,
        pets: pets.length > 0 ? pets : [{ name: '', type: '', breed: '', notes: '' }],
      };
    });
  };

  const lookupCep = async () => {
    const cleanCep = onlyDigits(registerData.zipCode);
    if (cleanCep.length !== 8) return;

    setCepLoading(true);
    setRegisterError('');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      if (!response.ok) {
        throw new Error('Erro de consulta');
      }

      const data = (await response.json()) as ViaCepResponse;
      if (data.erro) {
        throw new Error('CEP nao encontrado');
      }

      setRegisterData((prev) => ({
        ...prev,
        zipCode: cleanCep,
        street: data.logradouro || prev.street,
        neighborhood: data.bairro || prev.neighborhood,
        city: data.localidade || prev.city,
        state: data.uf || prev.state,
        complement: prev.complement || data.complemento || '',
      }));
    } catch {
      setRegisterError('Nao foi possivel localizar o CEP. Confira e tente novamente.');
    } finally {
      setCepLoading(false);
    }
  };

  const resetRegisterForm = () => {
    setRegisterData((prev) => ({
      ...initialRegisterForm,
      applicationType: prev.applicationType,
    }));
  };

  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');

    if (!registerData.fullName.trim()) {
      setRegisterError('Informe o nome completo.');
      return;
    }
    if (!registerData.email.trim()) {
      setRegisterError('Informe o e-mail.');
      return;
    }
    if (!registerData.phone.trim()) {
      setRegisterError('Informe o telefone.');
      return;
    }
    if (!registerData.zipCode.trim() || !registerData.street.trim()) {
      setRegisterError('Informe um CEP valido para preencher o endereco.');
      return;
    }
    if (!registerData.number.trim() || !registerData.neighborhood.trim()) {
      setRegisterError('Informe numero e bairro do endereco.');
      return;
    }
    if (!registerData.city.trim() || !registerData.state.trim()) {
      setRegisterError('Informe cidade e estado.');
      return;
    }
    if (
      registerData.applicationType === 'PROPERTY_SELLER' &&
      !registerData.propertyRegistrationNumber.trim()
    ) {
      setRegisterError('Para venda, a matricula/registro do imovel e obrigatoria.');
      return;
    }

    setRegisterLoading(true);

    try {
      const pets = registerData.hasPets
        ? registerData.pets
            .map((pet) => ({
              name: pet.name.trim() || undefined,
              type: pet.type.trim(),
              breed: pet.breed.trim() || undefined,
              notes: pet.notes.trim() || undefined,
            }))
            .filter((pet) => pet.type)
        : [];

      const payload: Record<string, unknown> = {
        applicationType: registerData.applicationType,
        fullName: registerData.fullName.trim(),
        email: registerData.email.trim().toLowerCase(),
        phone: registerData.phone.trim(),
        cpf: registerData.cpf.trim() || undefined,
        rg: registerData.rg.trim() || undefined,
        driverLicense: registerData.driverLicense.trim() || undefined,
        motherName: registerData.motherName.trim() || undefined,
        maritalStatus: registerData.maritalStatus || undefined,
        spouseName: registerData.spouseName.trim() || undefined,
        childrenNames: splitCommaList(registerData.childrenNamesInput),
        stepchildrenNames: splitCommaList(registerData.stepchildrenNamesInput),
        relativesNotes: registerData.relativesNotes.trim() || undefined,
        hasPets: registerData.hasPets,
        pets,
        zipCode: onlyDigits(registerData.zipCode),
        street: registerData.street.trim(),
        number: registerData.number.trim(),
        complement: registerData.complement.trim() || undefined,
        neighborhood: registerData.neighborhood.trim(),
        city: registerData.city.trim(),
        state: registerData.state.trim().toUpperCase(),
        employmentInfo: registerData.employmentInfo.trim() || undefined,
        intendedOccupants: registerData.intendedOccupants.trim() || undefined,
        rentalGuaranteePreference:
          registerData.rentalGuaranteePreference.trim() || undefined,
        propertyRegistrationNumber:
          registerData.propertyRegistrationNumber.trim() || undefined,
        hasActiveDebt: registerData.hasActiveDebt,
        activeDebtDetails: registerData.activeDebtDetails.trim() || undefined,
        isInPartition: registerData.isInPartition,
        isInInventory: registerData.isInInventory,
        legalIssuesDetails: registerData.legalIssuesDetails.trim() || undefined,
        propertyDocumentationNotes:
          registerData.propertyDocumentationNotes.trim() || undefined,
        additionalComplianceInfo:
          registerData.additionalComplianceInfo.trim() || undefined,
      };

      if (registerData.monthlyIncome.trim()) {
        payload.monthlyIncome = Number(registerData.monthlyIncome.replace(',', '.'));
      }

      await api.post('/customer-onboarding/public/applications', payload);

      setRegisterSuccess(
        'Cadastro enviado com sucesso. Aguarde a aprovacao da administracao para avancar com as consultas de compliance.',
      );
      resetRegisterForm();
    } catch (error: unknown) {
      setRegisterError(parseError(error, 'Nao foi possivel enviar o cadastro.'));
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-primary-100 py-8 px-4">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="hidden lg:flex flex-col justify-between card bg-primary-700 text-white border-none">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white/20 p-3 rounded-xl">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" style={{ width: logoWidth, maxWidth: '100%' }} className="object-contain" />
                ) : (
                  <Building2 className="w-10 h-10" />
                )}
              </div>
              <div>
                <h1 className="text-3xl font-bold">MacroImob</h1>
                <p className="text-primary-100">Gestao completa para imobiliaria</p>
              </div>
            </div>

            <p className="text-primary-50 leading-relaxed">
              Login administrativo e cadastro completo de cliente na mesma tela. Os
              cadastros entram no fluxo de compliance com aprovacao interna antes das
              consultas de SPC, Serasa e Justica.
            </p>
          </div>

          <div className="space-y-3 mt-8">
            <div className="flex items-center gap-2 text-primary-50">
              <ShieldCheck className="w-4 h-4" />
              Fluxo de aprovacao obrigatorio
            </div>
            <div className="flex items-center gap-2 text-primary-50">
              <MapPin className="w-4 h-4" />
              Endereco preenchido por CEP
            </div>
            <div className="flex items-center gap-2 text-primary-50">
              <UserPlus className="w-4 h-4" />
              Cadastro completo para locacao e venda
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-center mb-6">
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setActiveTab('login')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'login'
                    ? 'bg-white shadow text-primary-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Entrar
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('register')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'register'
                    ? 'bg-white shadow text-primary-700'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Cadastro do Cliente
              </button>
            </div>
          </div>

          {activeTab === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="text-center mb-2">
                <h2 className="text-xl font-bold">Acesso Administrativo</h2>
                <p className="text-sm text-gray-600">Entre para gerir o sistema</p>
              </div>

              <div>
                <label className="label">E-mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="label">Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="input"
                  required
                />
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {loginError}
                </div>
              )}

              <button type="submit" disabled={loginLoading} className="btn-primary w-full">
                {loginLoading ? 'Entrando...' : 'Entrar'}
              </button>

            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-5 max-h-[75vh] overflow-y-auto pr-2">
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Tipo de Cadastro</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => updateField('applicationType', 'RENTAL_TENANT')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      registerData.applicationType === 'RENTAL_TENANT'
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'border-gray-300 text-gray-700'
                    }`}
                  >
                    Locacao (Locatario)
                  </button>
                  <button
                    type="button"
                    onClick={() => updateField('applicationType', 'PROPERTY_SELLER')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      registerData.applicationType === 'PROPERTY_SELLER'
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'border-gray-300 text-gray-700'
                    }`}
                  >
                    Venda (Proprietario)
                  </button>
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Dados Pessoais</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    className="input md:col-span-2"
                    placeholder="Nome completo"
                    value={registerData.fullName}
                    onChange={(event) => updateField('fullName', event.target.value)}
                    required
                  />
                  <input
                    type="email"
                    className="input"
                    placeholder="E-mail (usuario de acesso)"
                    value={registerData.email}
                    onChange={(event) => updateField('email', event.target.value)}
                    required
                  />
                  <input
                    className="input"
                    placeholder="Telefone"
                    value={registerData.phone}
                    onChange={(event) => updateField('phone', event.target.value)}
                    required
                  />
                  <input
                    className="input"
                    placeholder="CPF"
                    value={registerData.cpf}
                    onChange={(event) => updateField('cpf', event.target.value)}
                  />
                  <input
                    className="input"
                    placeholder="RG"
                    value={registerData.rg}
                    onChange={(event) => updateField('rg', event.target.value)}
                  />
                  <input
                    className="input"
                    placeholder="Carteira de motorista (CNH)"
                    value={registerData.driverLicense}
                    onChange={(event) => updateField('driverLicense', event.target.value)}
                  />
                  <input
                    className="input md:col-span-2"
                    placeholder="Nome da mae"
                    value={registerData.motherName}
                    onChange={(event) => updateField('motherName', event.target.value)}
                  />
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Endereco (com CEP)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <input
                      className="input"
                      placeholder="CEP"
                      value={registerData.zipCode}
                      onChange={(event) => updateField('zipCode', event.target.value)}
                      onBlur={() => void lookupCep()}
                      required
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-secondary md:col-span-2"
                    onClick={() => void lookupCep()}
                    disabled={cepLoading}
                  >
                    {cepLoading ? 'Consultando CEP...' : 'Buscar endereco pelo CEP'}
                  </button>
                  <input
                    className="input md:col-span-2"
                    placeholder="Rua"
                    value={registerData.street}
                    onChange={(event) => updateField('street', event.target.value)}
                    required
                  />
                  <input
                    className="input"
                    placeholder="Numero"
                    value={registerData.number}
                    onChange={(event) => updateField('number', event.target.value)}
                    required
                  />
                  <input
                    className="input md:col-span-2"
                    placeholder="Complemento"
                    value={registerData.complement}
                    onChange={(event) => updateField('complement', event.target.value)}
                  />
                  <input
                    className="input"
                    placeholder="Bairro"
                    value={registerData.neighborhood}
                    onChange={(event) => updateField('neighborhood', event.target.value)}
                    required
                  />
                  <input
                    className="input"
                    placeholder="Cidade"
                    value={registerData.city}
                    onChange={(event) => updateField('city', event.target.value)}
                    required
                  />
                  <input
                    className="input"
                    placeholder="UF"
                    value={registerData.state}
                    onChange={(event) => updateField('state', event.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Familia e Relacoes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <select
                    className="input"
                    value={registerData.maritalStatus}
                    onChange={(event) =>
                      updateField('maritalStatus', event.target.value as MaritalStatus | '')
                    }
                  >
                    <option value="">Estado civil</option>
                    <option value="SINGLE">Solteiro(a)</option>
                    <option value="MARRIED">Casado(a)</option>
                    <option value="STABLE_UNION">Uniao estavel</option>
                    <option value="DIVORCED">Divorciado(a)</option>
                    <option value="WIDOWED">Viuvo(a)</option>
                  </select>
                  <input
                    className="input"
                    placeholder="Nome do conjuge (se houver)"
                    value={registerData.spouseName}
                    onChange={(event) => updateField('spouseName', event.target.value)}
                  />
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Filhos (separe por virgula)"
                    value={registerData.childrenNamesInput}
                    onChange={(event) => updateField('childrenNamesInput', event.target.value)}
                  />
                  <textarea
                    className="input"
                    rows={2}
                    placeholder="Enteados (separe por virgula)"
                    value={registerData.stepchildrenNamesInput}
                    onChange={(event) =>
                      updateField('stepchildrenNamesInput', event.target.value)
                    }
                  />
                  <textarea
                    className="input md:col-span-2"
                    rows={2}
                    placeholder="Outros parentes e contatos relevantes"
                    value={registerData.relativesNotes}
                    onChange={(event) => updateField('relativesNotes', event.target.value)}
                  />
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Pets</h3>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={registerData.hasPets}
                    onChange={(event) => updateField('hasPets', event.target.checked)}
                  />
                  Cliente possui pet(s)
                </label>

                {registerData.hasPets && (
                  <div className="space-y-3">
                    {registerData.pets.map((pet, index) => (
                      <div key={`pet-${index}`} className="border rounded-lg p-3 space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            className="input"
                            placeholder="Nome do pet"
                            value={pet.name}
                            onChange={(event) => updatePet(index, 'name', event.target.value)}
                          />
                          <input
                            className="input"
                            placeholder="Tipo (ex: cachorro, gato)"
                            value={pet.type}
                            onChange={(event) => updatePet(index, 'type', event.target.value)}
                          />
                          <input
                            className="input"
                            placeholder="Raca"
                            value={pet.breed}
                            onChange={(event) => updatePet(index, 'breed', event.target.value)}
                          />
                          <input
                            className="input"
                            placeholder="Observacoes"
                            value={pet.notes}
                            onChange={(event) => updatePet(index, 'notes', event.target.value)}
                          />
                        </div>
                        <button
                          type="button"
                          className="text-red-600 text-sm flex items-center gap-1"
                          onClick={() => removePet(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                          Remover pet
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addPet}
                      className="btn-secondary w-full flex items-center justify-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar pet
                    </button>
                  </div>
                )}
              </div>

              {registerData.applicationType === 'RENTAL_TENANT' ? (
                <div className="border rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-gray-900">Dados para Locacao</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      className="input"
                      placeholder="Profissao/empresa"
                      value={registerData.employmentInfo}
                      onChange={(event) => updateField('employmentInfo', event.target.value)}
                    />
                    <input
                      className="input"
                      placeholder="Renda mensal (ex: 4500.00)"
                      value={registerData.monthlyIncome}
                      onChange={(event) => updateField('monthlyIncome', event.target.value)}
                    />
                    <input
                      className="input md:col-span-2"
                      placeholder="Quem vai morar no imovel"
                      value={registerData.intendedOccupants}
                      onChange={(event) =>
                        updateField('intendedOccupants', event.target.value)
                      }
                    />
                    <input
                      className="input md:col-span-2"
                      placeholder="Garantia preferida (fiador, seguro-fianca, caucao...)"
                      value={registerData.rentalGuaranteePreference}
                      onChange={(event) =>
                        updateField('rentalGuaranteePreference', event.target.value)
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="border rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-gray-900">Dados para Venda</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      className="input md:col-span-2"
                      placeholder="Matricula/registro do imovel"
                      value={registerData.propertyRegistrationNumber}
                      onChange={(event) =>
                        updateField('propertyRegistrationNumber', event.target.value)
                      }
                      required
                    />
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={registerData.hasActiveDebt}
                        onChange={(event) =>
                          updateField('hasActiveDebt', event.target.checked)
                        }
                      />
                      Imovel possui divida ativa
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={registerData.isInPartition}
                        onChange={(event) =>
                          updateField('isInPartition', event.target.checked)
                        }
                      />
                      Imovel em partilha
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 md:col-span-2">
                      <input
                        type="checkbox"
                        checked={registerData.isInInventory}
                        onChange={(event) =>
                          updateField('isInInventory', event.target.checked)
                        }
                      />
                      Imovel em inventario
                    </label>
                    <textarea
                      className="input md:col-span-2"
                      rows={2}
                      placeholder="Detalhes de dividas ativas"
                      value={registerData.activeDebtDetails}
                      onChange={(event) =>
                        updateField('activeDebtDetails', event.target.value)
                      }
                    />
                    <textarea
                      className="input md:col-span-2"
                      rows={2}
                      placeholder="Pendencias judiciais"
                      value={registerData.legalIssuesDetails}
                      onChange={(event) =>
                        updateField('legalIssuesDetails', event.target.value)
                      }
                    />
                    <textarea
                      className="input md:col-span-2"
                      rows={2}
                      placeholder="Observacoes de documentacao do imovel"
                      value={registerData.propertyDocumentationNotes}
                      onChange={(event) =>
                        updateField('propertyDocumentationNotes', event.target.value)
                      }
                    />
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Observacoes Gerais</h3>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Demais informacoes relevantes para analise de compliance"
                  value={registerData.additionalComplianceInfo}
                  onChange={(event) =>
                    updateField('additionalComplianceInfo', event.target.value)
                  }
                />
              </div>

              {registerError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {registerError}
                </div>
              )}

              {registerSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  {registerSuccess}
                </div>
              )}

              <button type="submit" className="btn-primary w-full" disabled={registerLoading}>
                {registerLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando cadastro...
                  </span>
                ) : (
                  'Enviar cadastro para aprovacao'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
